import { ref, reactive, computed, defineAsyncComponent } from 'vue'
import { useRouter } from 'vue-router'
import { mapActions, useStore } from "vuex";
import { useHttp } from './http'
import { usePages } from './pages'

export function useElements() {

    const { errors: pageErrors,
        page,
        getFirstPageOrCreate
    } = usePages()

    const $axios = useHttp();
    const element = ref({})
    let pageId = ref();
    const errors = ref('')
    const store = useStore()
    const elements = computed(() => store.getters['elements/getElements'])

    const capitalize = (str) => {
        if (typeof str !== 'undefined') { return str.charAt(0).toUpperCase() + str.slice(1); }
        return str;
    }

    const excludeElementsForCountries = {
        "instagram": ["rus"],
        "checkoutStripe": ["rus"],
    };

    const limitElementsForCountry = {
        "checkoutYandex": ["rus"],
        "rutube": ["rus"]
    };

    const categoryOrder = ['text', 'media', 'buttons', 'contact', 'navigation', 'documents', 'integrations', 'ecommerce'];

    const categoryTitles = {
        text: 'elementCategories.text',
        media: 'elementCategories.media',
        buttons: 'elementCategories.buttons',
        contact: 'elementCategories.contact',
        navigation: 'elementCategories.navigation',
        documents: 'elementCategories.documents',
        integrations: 'elementCategories.integrations',
        ecommerce: 'elementCategories.ecommerce',
    };

    const availableElementTypes = () => {
        const country = import.meta.env.PUBLIC_APP_COUNTRY ? import.meta.env.PUBLIC_APP_COUNTRY : 'rus';

        return availableElementTypesArray.filter((el) => {
            return (
                typeof excludeElementsForCountries[el.type] === 'undefined' ||
                excludeElementsForCountries[el.type].findIndex((cntry) => cntry === country ) === -1
            )
            &&
            (
                typeof limitElementsForCountry[el.type] === 'undefined' ||
                limitElementsForCountry[el.type].findIndex((cntr) => cntr === country) !== -1
            )
        });
    }

    const availableElementTypesByCategory = () => {
        const elements = availableElementTypes();
        const grouped = {};

        // Group elements by category
        elements.forEach((el) => {
            const category = el.category || 'other';
            if (!grouped[category]) {
                grouped[category] = [];
            }
            grouped[category].push(el);
        });

        // Sort categories according to categoryOrder
        const sorted = [];
        categoryOrder.forEach((category) => {
            if (grouped[category]) {
                sorted.push({
                    category,
                    title: categoryTitles[category] || category,
                    elements: grouped[category],
                });
            }
        });

        // Add remaining categories not in categoryOrder
        Object.keys(grouped).forEach((category) => {
            if (!categoryOrder.includes(category)) {
                sorted.push({
                    category,
                    title: categoryTitles[category] || category,
                    elements: grouped[category],
                });
            }
        });

        return sorted;
    }

    const availableElementTypesArray = [
        // Text Elements
        { available: true, type: "header", icon: "icons8:header", title: "elements.header", category: "text" },
        { available: true, type: "text", icon: "cil:text-square", title: "elements.text", category: "text" },
        { available: true, type: "markdown", icon: "mdi:language-markdown-outline", title: "elements.markdown", category: "text" },
        { available: true, type: "textCard", icon: "solar:card-2-outline", title: "elements.card", category: "text" },
        { available: true, type: "list", icon: "bi:list-ul", title: "elements.list", category: "text" },
        { available: true, type: "animatedCounter", icon: "mdi:counter", title: "elements.animatedCounter", category: "text" },

        // Images & Media
        { available: true, type: "avatar", icon: "mdi:user", title: "elements.avatar", category: "media" },
        { available: true, type: "image", icon: "bi:image-fill", title: "elements.image", category: "media" },
        { available: true, type: "gallery", icon: "mdi:view-gallery-outline", title: "elements.gallery", category: "media" },
        { available: true, type: "audioPlayer", icon: "material-symbols:audio-file-outline", title: "elements.audioPlayer", category: "media" },
        { available: true, type: "youtube", icon: "fa:youtube", title: "elements.youtube", category: "media" },
        { available: true, type: "videoPlayer", icon: "ph:video", title: "elements.videoPlayer", category: "media" },
        { available: true, type: "spotifyPodcast", icon: "mdi:spotify", title: "elements.spotifyPodcast", category: "media" },
        { available: false, type: "instagram", icon: "bi:instagram", title: "elements.instagram", category: "media" },
        { available: false, type: "rutube", icon: "arcticons:adobe-premiere-rush", title: "elements.rutube", category: "media" },

        // Buttons & Links
        { available: true, type: "button", icon: "carbon:button-centered", title: "elements.button", category: "buttons" },
        { available: true, type: "buttonsGroup", icon: "fluent:dual-screen-group-20-filled", title: "elements.buttonsGroup", category: "buttons" },
        { available: true, type: "listButton", icon: "carbon:list-dropdown", title: "elements.listButton", category: "buttons" },
        { available: true, type: "linksList", icon: "tdesign:tree-list", title: "elements.linksList", category: "buttons" },
        { available: true, type: "socialButtons", icon: "tabler:social", title: "elements.socialButtons", category: "buttons" },
        { available: false, type: "shares", icon: "ic:outline-share", title: "elements.shares", category: "buttons" },

        // Contact & Forms
        { available: true, type: "email", icon: "mdi:email-fast-outline", title: "elements.email", category: "contact" },
        { available: true, type: "telephone", icon: "mdi:phone", title: "elements.telephone", category: "contact" },
        { available: true, type: "contactForm", icon: "tdesign:form", title: "elements.contactForm", category: "contact" },

        // Navigation & Layout
        { available: true, type: "menu", icon: "gg:menu-round", title: "elements.menu", category: "navigation" },
        { available: true, type: "accordion", icon: "vaadin:accordion-menu", title: "elements.accordion", category: "navigation" },
        { available: false, type: "hr", icon: "bi:hr", title: "elements.hr", category: "navigation" },
        { available: true, type: "space", icon: "mdi:arrow-expand-vertical", title: "elements.space", category: "navigation" },

        // Documents & Data
        { available: true, type: "pdfViewer", icon: "bi:book", title: "elements.pdfViewer", category: "documents" },
        { available: false, type: "pdfFile", icon: "bi:file-earmark-pdf", title: "elements.pdfFile", category: "documents" },
        { available: true, type: "csvTable", icon: "material-symbols:csv", title: "elements.csvTable", category: "documents" },

        // Integrations
        { available: true, type: "map", icon: "bi:map", title: "elements.map", category: "integrations" },
        { available: true, type: "iframe", icon: "mdi:iframe-array-outline", title: "elements.iframe", category: "integrations" },
        { available: false, type: "modalWindowText", icon: "carbon:popup", title: "elements.modalWindowText", category: "integrations" },
        { available: false, type: "modalWindowIframe", icon: "material-symbols-light:iframe-sharp", title: "elements.modalWindowIframe", category: "integrations" },
        { available: true, type: "cookies", icon: "fluent:cookies-16-regular", title: "elements.cookiesConsent", category: "integrations" },

        // E-commerce
        { available: true, type: "schedule", icon: "uil:schedule", title: "elements.schedule", category: "ecommerce" },
        { available: true, type: "servicesList", icon: "mdi:format-list-bulleted-type", title: "elements.servicesList", category: "ecommerce" },
        { available: false, type: "products", icon: "gg:menu-round", title: "elements.products", category: "ecommerce" },
        { available: false, type: "order", icon: "bi:cash-stack", title: "elements.order", category: "ecommerce" },
        { available: false, type: "checkoutStripe", icon: "material-symbols:shopping-cart-checkout", title: "elements.checkoutStripe", category: "ecommerce" },
        { available: false, type: "checkoutYandex", icon: "material-symbols:shopping-cart-checkout", title: "elements.checkoutYandex", category: "ecommerce" },
        { available: false, type: "card", icon: "solar:card-linear", title: "elements.card", category: "ecommerce" },
    ];

    const getElementById = (elementId) => {
        const elementIndex = Object.keys(elements.value).findIndex(
            (v, key) => elements.value[key].id === parseInt(elementId)
        )
        if (elementIndex > -1) {
            return elements.value[elementIndex]
        } else { return undefined };
    }

    const fetchElements = async (pageId, websiteId) => {
        console.log('get element for the page', pageId);
        if (typeof pageId === 'undefined') {
            const firstPage = await getFirstPageOrCreate(websiteId);
            pageId = firstPage.id;
        }

        const els = await $axios.get(`/pages/${pageId}/elements`).then(async (response) => {
            console.log('returned elements', response)
            const sorted = sortByOrder(response.data.data);
            store.commit('elements/SET_ELEMENTS', sorted);
            store.commit('elements/SET_PAGE_ID', pageId);

            return sorted;
        })
        return els;
    }

    const getElementsFromStore = () => sortByOrder(store.getters['elements/getElements']);

    const sortByOrder = (arr) => arr.sort((a, b) => a.order - b.order)

    const getPageId = () => pageId.value;

    const storeElement = async (after, data) => {
        errors.value = ''
        console.log(`after ${after}`);
        try {
            return await $axios.post(`pages/${data.pageId}/elements/`, data)
                .then(async ({ data }) => {
                    console.log('store succsesful', data);
                    await store.dispatch('elements/insertElement', { element: data.data, after: after });

                    // TODO: send patch/batch request to update the order of all elements
                    // it's requred because after inserting a new element the orders of all elements
                    // that are placed after it will be shifted

                    const orderElementsData = {
                        "resources": {}
                    };
                    Object.values(store.getters['elements/getElements']).forEach((el) => orderElementsData.resources[el.id] = el);
                    await $axios.patch(`pages/${data.data.pageId}/elements/batch`, orderElementsData, {
                        headers: {
                            'Request-Origin-Timestamp': Date.now(),
                            'Request-Origin-Entity': `page-${data.data.pageId}-fullorderbatch`
                        }
                    })

                    return data.data
                })
        } catch (e) {
            console.log(e);
            if (e.response.status === 422) {
                for (const key in e.response.data.errors) {
                    errors.value += e.response.data.errors[key][0] + ' ';
                }
            }
        }
    }

    const solveOrderConflicts = async (pageId) => {
        let i = 0; let resources = {};
        getElementsFromStore().map((element) => {
            element.order = i;
            i++;
            resources[element.id] = element;
        })

        const patchData = {
            resources
        };

        return await $axios.patch(`pages/${pageId}/elements/batch`, patchData, {
            headers: {
                'Request-Origin-Timestamp': Date.now(),
                'Request-Origin-Entity': `page-${pageId}-orderbatch`
            }
        })
            .then(() => {
                return fetchElements(pageId)
            })
            .catch(async () => {
                console.log('the fix was failed')
            });
    }


    const reorderElement = async (pageId, current, neighbour) => {
        if (typeof current === "undefined") {
            console.log('main element doesn\'t passed')
            return;
        }
        if (typeof neighbour === "object" && typeof neighbour.order !== "undefined") {

            // check and try to solve a conflict
            if (current.order === neighbour.order) {
                solveOrderConflicts(pageId);
                alert('Order was fixed, try again')
            } else {
                // store dump to future rollback in the case of an error
                const currentDump = { ...current },
                    neighbourDump = { ...neighbour };

                // change order
                neighbour.order = currentDump.order;
                current.order = neighbourDump.order;

                // prepare new objects
                const patchData = {
                    "resources": {
                        [current.id]: current,
                        [neighbour.id]: neighbour,
                    }
                };

                // patch objects in state
                await store.dispatch('elements/replaceElement', { ...current })
                await store.dispatch('elements/replaceElement', { ...neighbour })

                // patch objects on backend
                await $axios.patch(`pages/${pageId}/elements/batch`, patchData)
                    .catch(async () => {
                        // rollback swapping in the case of en error
                        console.log('roll back order changes')
                        // unpatch objects in state
                        store.dispatch('elements/replaceElement', { currentDump })
                        store.dispatch('elements/replaceElement', { neighbourDump })
                    });
            }
        } else {
            console.log('you have to pass only or at least one neighbour element')
            return;
        }
    }

    const updateElement = async (data) => {
        console.log('update data', JSON.parse(JSON.stringify(data)))
        errors.value = ''
        try {
            console.log('update el');
            await $axios.patch(`pages/${data.pageId}/elements/${data.id}`,
                data,
                {
                    headers: {
                        'Request-Origin-Timestamp': Date.now(),
                        'Request-Origin-Entity': `element-${data.id}-update`
                    }
                }
            );
            await store.dispatch('elements/replaceElement', { ...data });
            return data.data;
        } catch (e) {
            console.log(e);
            if (e.response.status === 422) {
                for (const key in e.response.data.errors) {
                    errors.value += e.response.data.errors[key][0] + ' ';
                }
            }
        }
    }

    const deleteElement = async (element) => {
        await $axios.delete(`pages/${element.pageId}/elements/${element.id}`,
            {
                headers: {
                    'Request-Origin-Timestamp': Date.now(),
                    'Request-Origin-Entity': `element-${element.id}-delete`

                }
            }).then(() => {
                store.dispatch('elements/deleteElement', element);
            })
    }

    const renderElement = (el, type) => {
        if (typeof el === 'undefined') {
            return;
        }

        if (typeof type === 'undefined') {
            type = 'template';
        }

        const cEl = capitalize(el);
        const cType = capitalize(type);
        console.log(`../components/Elements/HTMLElements/${cEl}Element/${cEl}${cType}.vue`);
        
        return defineAsyncComponent({
            loader: () =>
                import(`../components/Elements/HTMLElements/${cEl}Element/${cEl}${cType}.vue`),

            // TODO: add a component to use if the load fails
            // errorComponent: ErrorComponent
        });
    };

    return {
        errors,
        availableElementTypes,
        availableElementTypesByCategory,
        element,
        elements,
        pageId,
        getPageId,
        getElementById,
        fetchElements,
        getElementsFromStore,
        storeElement,
        updateElement,
        deleteElement,
        renderElement,
        reorderElement,
    }
}
